/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { PRIVATE_OFFER_STATUS, PrivateOffer, PrivateOfferStatus } from '@/types/privateOffer'
import { Button, Space, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

export const usePrivateOffersColumns = (): ColumnsType<PrivateOffer> => {
  const { t } = useTranslation()
  const { contractId } = useParams()

  const getStatusConfig = (status: PrivateOfferStatus) => {
    // 统一状态颜色映射规范，与管理端完全一致使用Ant Design语义化颜色
    const statusMap: Record<PrivateOfferStatus, { color: string, text: string }> = {
      [PRIVATE_OFFER_STATUS.DRAFT]: { color: 'default', text: t('Draft') }, // 默认色
      [PRIVATE_OFFER_STATUS.PENDING_APPROVAL]: { color: 'processing', text: t('Pending Approval') }, // 蓝色
      [PRIVATE_OFFER_STATUS.APPROVED]: { color: 'warning', text: t('Approved') }, // 橙色
      [PRIVATE_OFFER_STATUS.PENDING_ACCEPTANCE]: { color: 'warning', text: t('Pending Acceptance') }, // 橙色
      [PRIVATE_OFFER_STATUS.ACCEPTED]: { color: 'success', text: t('Accepted') }, // 绿色
      [PRIVATE_OFFER_STATUS.REJECTED]: { color: 'error', text: t('Rejected') }, // 红色
      [PRIVATE_OFFER_STATUS.EXPIRED]: { color: 'default', text: t('Expired') }, // 灰色
      [PRIVATE_OFFER_STATUS.INVALID]: { color: 'default', text: t('Invalid') }, // 灰色
    }
    return statusMap[status as PrivateOfferStatus] || { color: 'default', text: status }
  }

  // Format timestamp to readable date
  const formatTimestamp = (timestamp: number | undefined): string => {
    if (!timestamp) return '-'
    return dayjs(timestamp * 1000).format('YYYY/MM/DD') // Convert to milliseconds
  }

  // Format ISO string to readable date
  const formatISOString = (isoString: string | undefined): string => {
    if (!isoString) return '-'
    return dayjs(isoString).format('YYYY/MM/DD')
  }

  return [
    {
      title: t('Offer ID'),
      dataIndex: 'id',
      key: 'offerId',
      width: 180,
      render: (offerId: string | undefined) => offerId ? (
        <Link to={`/billing-center/${contractId}/private-offers/${offerId}`}>
          {offerId}
        </Link>
      ) : '-',
    },
    // 移除Contract Info列，避免404错误且对客户无实际意义
    // {
    //   title: t('Contract Info'),
    //   dataIndex: 'contractId',
    //   key: 'contractInfo',
    //   width: 200,
    //   render: (contractId: string | undefined, record: PrivateOffer) => (
    //     <div>
    //       <div>
    //         <Link to={`/billing-center/${contractId}/contracts`}>
    //           {contractId || '-'}
    //         </Link>
    //       </div>
    //       {/* Contract tags or additional info can be added here */}
    //     </div>
    //   ),
    // },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: PrivateOfferStatus) => {
        const config = getStatusConfig(status)
        return <Tag color={config.color}>{config.text}</Tag>
      },
    },
    {
      title: t('Effective Time'),
      dataIndex: 'effectiveDate',
      key: 'effectiveTime',
      width: 140,
      render: (effectiveDate: number | undefined) => formatTimestamp(effectiveDate),
    },
    {
      title: t('Customer Confirmation Deadline'),
      dataIndex: 'expirationDate',
      key: 'expiration',
      width: 220,
      render: (expiration: number | undefined, record: PrivateOffer) => {
        if (record.status === PRIVATE_OFFER_STATUS.ACCEPTED) {
          return '-'
        }
        return formatTimestamp(expiration)
      },
    },
    // 移除Creator列，避免泄露内部员工信息
    // {
    //   title: t('Creator'),
    //   dataIndex: 'creatorName',
    //   key: 'creator',
    //   width: 120,
    //   render: (creatorName: string | undefined, record: PrivateOffer) =>
    //     creatorName || record.creatorId || '-',
    // },
    {
      title: t('Created At'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (createdAt: string | undefined) => formatISOString(createdAt),
    },
    {
      title: t('Actions'),
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record: PrivateOffer) => (
        <Space>
          <Button type="link" size="small">
            <Link to={`/billing-center/${contractId}/private-offers/${record.id}`}>
              {t('Detail')}
            </Link>
          </Button>
        </Space>
      ),
    },
  ]
}